import { useState, useEffect } from 'react';
import { dataApi } from '@/lib/api';
import { Search, Download, ChevronLeft, ChevronRight, X, User, Building, Mail, MapPin, Briefcase } from 'lucide-react';

function formatDate(d) {
  if (!d) return '-';
  return d.split(' ')[0];
}

export default function LeadsManagement() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [regionFilter, setRegionFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [detailLead, setDetailLead] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(true);
      dataApi.getLeads({
        campaign: campaignFilter || undefined,
        status: statusFilter || undefined,
        region: regionFilter || undefined,
        search: search || undefined,
        page,
      })
        .then(r => {
          setLeads(r.data.leads);
          setTotal(r.data.total);
          setPages(r.data.pages);
        })
        .catch(() => {})
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [statusFilter, regionFilter, campaignFilter, search, page]);

  const toggleSelect = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setSelected(next);
  };

  const toggleAll = () => {
    if (selected.size === leads.length) setSelected(new Set());
    else setSelected(new Set(leads.map(l => l.id)));
  };

  const exportCsv = () => {
    const header = 'Name,Company,Job Role,Campaign,Status,Assigned To,Last Seen\n';
    const rows = leads.map(l =>
      `"${l.first_name} ${l.last_name}","${l.company}","${l.job_role}","${l.campaign}","${l.status}","${l.assigned_to}","${l.last_seen}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'leads.csv'; a.click();
  };

  const statusColor = {
    'Active': 'active',
    'Won': 'won',
    'Lost': 'lost',
    'New': 'new',
  };

  return (
    <div className="space-y-6" data-testid="leads-management-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold text-[#0A0A0A] tracking-tight">Leads Management</h1>
        <button data-testid="leads-export-btn" onClick={exportCsv} className="flex items-center gap-2 px-4 py-2 rounded-lg border border-neutral-200 bg-white text-sm font-medium text-neutral-600 hover:bg-neutral-50">
          <Download className="w-4 h-4" /> Export CSV
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-300" />
          <input data-testid="leads-search" value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}
            placeholder="Search leads..." className="w-full h-9 pl-10 pr-3 rounded-lg border border-neutral-200 bg-white text-sm focus:ring-2 focus:ring-[#F66922] outline-none" />
        </div>
        <select data-testid="leads-status-filter" value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }}
          className="h-9 px-3 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-600">
          <option value="">All Statuses</option>
          <option value="New">New</option>
          <option value="Qualified">Active</option>
          <option value="Won">Won</option>
          <option value="Lost">Lost</option>
        </select>
        <select data-testid="leads-region-filter" value={regionFilter} onChange={e => { setRegionFilter(e.target.value); setPage(1); }}
          className="h-9 px-3 rounded-lg border border-neutral-200 bg-white text-sm text-neutral-600">
          <option value="">All Regions</option>
          <option value="NA">NA</option>
          <option value="EMEA">EMEA</option>
          <option value="APAC">APAC</option>
          <option value="LATAM">LATAM</option>
        </select>
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-blue-50 border border-blue-100 rounded-lg px-4 py-2" data-testid="bulk-actions">
          <span className="text-xs text-blue-700 font-medium">{selected.size} selected</span>
          <button className="px-3 py-1 text-xs bg-[#F66922] text-white rounded-md font-medium">Assign</button>
          <button onClick={exportCsv} className="px-3 py-1 text-xs bg-white border border-neutral-200 text-neutral-600 rounded-md font-medium">Export</button>
          <button className="px-3 py-1 text-xs bg-white border border-neutral-200 text-neutral-600 rounded-md font-medium">Update Status</button>
        </div>
      )}

      {/* Table */}
      <div className="oaa-card overflow-hidden">
        {loading ? (
          <div className="h-96 skeleton-shimmer" />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table w-full">
                <thead>
                  <tr>
                    <th className="w-8">
                      <input type="checkbox" checked={selected.size === leads.length && leads.length > 0}
                        onChange={toggleAll} className="rounded border-slate-300" />
                    </th>
                    <th>Name</th>
                    <th>Company</th>
                    <th>Job Role</th>
                    <th>Campaign</th>
                    <th>Status</th>
                    <th>Assigned To</th>
                    <th>Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l, i) => (
                    <tr key={i} className="cursor-pointer" onClick={() => setDetailLead(l)} data-testid={`lead-row-${i}`}>
                      <td onClick={e => e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(l.id)} onChange={() => toggleSelect(l.id)} className="rounded border-slate-300" />
                      </td>
                      <td className="font-medium text-[#F66922]">{l.first_name} {l.last_name}</td>
                      <td className="text-neutral-600">{l.company}</td>
                      <td className="text-neutral-500 max-w-[150px] truncate">{l.job_role}</td>
                      <td className="text-neutral-500 max-w-[200px] truncate">{l.campaign?.substring(0, 40)}</td>
                      <td><span className={`status-badge ${statusColor[l.status] || 'active'}`}>{l.status}</span></td>
                      <td className="text-neutral-500 text-xs">{l.assigned_to}</td>
                      <td className="text-neutral-400 text-xs">{formatDate(l.last_seen)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-neutral-100">
              <span className="text-xs text-neutral-400">{total.toLocaleString()} leads total</span>
              <div className="flex items-center gap-2">
                <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"><ChevronLeft className="w-4 h-4" /></button>
                <span className="text-xs text-neutral-500">Page {page} of {pages}</span>
                <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="p-1.5 rounded border border-neutral-200 hover:bg-neutral-50 disabled:opacity-40"><ChevronRight className="w-4 h-4" /></button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Detail Side Panel */}
      {detailLead && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="lead-detail-panel">
          <div className="absolute inset-0 bg-black/30" onClick={() => setDetailLead(null)} />
          <div className="relative w-full max-w-md bg-white shadow-xl overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between z-10">
              <h3 className="text-base font-semibold text-[#0A0A0A]">Lead Details</h3>
              <button onClick={() => setDetailLead(null)} className="p-1 rounded hover:bg-neutral-100"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-[#F66922] text-white flex items-center justify-center text-lg font-bold">
                  {(detailLead.first_name || '?')[0]}
                </div>
                <div>
                  <p className="text-base font-semibold text-[#0A0A0A]">{detailLead.first_name} {detailLead.last_name}</p>
                  <p className="text-xs text-neutral-400">{detailLead.job_role}</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm text-neutral-500"><Mail className="w-4 h-4 text-neutral-300" />{detailLead.email || '-'}</div>
                <div className="flex items-center gap-2 text-sm text-neutral-500"><Building className="w-4 h-4 text-neutral-300" />{detailLead.company}</div>
                <div className="flex items-center gap-2 text-sm text-neutral-500"><MapPin className="w-4 h-4 text-neutral-300" />{detailLead.country}</div>
                <div className="flex items-center gap-2 text-sm text-neutral-500"><Briefcase className="w-4 h-4 text-neutral-300" />{detailLead.industry}</div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Campaign</h4>
                <p className="text-sm text-neutral-600">{detailLead.campaign}</p>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Status</h4>
                <span className={`status-badge ${statusColor[detailLead.status] || 'active'}`}>{detailLead.status}</span>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Details</h4>
                <div className="space-y-1 text-sm text-neutral-500">
                  <p>Job Function: {detailLead.JobFunction || '-'}</p>
                  <p>Job Level: {detailLead.JobLevel || '-'}</p>
                  <p>Headcount: {detailLead.headcount || '-'}</p>
                  <p>Revenue: {detailLead.revenue || '-'}</p>
                  <p>Assigned to: {detailLead.assigned_to || '-'}</p>
                  <p>Last Seen: {formatDate(detailLead.last_seen)}</p>
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-neutral-400 uppercase tracking-wider mb-2">Content Engaged</h4>
                <p className="text-sm text-neutral-600">{detailLead.content_engaged || '-'}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
